// src/pages/NotFound.jsx
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { Home, ArrowBack } from '@mui/icons-material';
import AuthContext from '../contexts/AuthContext';

const NotFound = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleGoHome = () => {
    if (user) {
      // Redirect to appropriate dashboard based on role
      if (user.role === 'admin') {
        navigate('/admin/strategies');
      } else {
        navigate('/officer/dashboard');
      }
    } else {
      navigate('/login');
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            textAlign: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {/* 404 Text */}
          <Typography
            sx={{
              fontSize: { xs: '8rem', sm: '12rem', md: '16rem' },
              fontWeight: 900,
              color: 'rgba(255, 255, 255, 0.2)',
              lineHeight: 1,
              mb: 2,
              textShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
              letterSpacing: '-0.05em',
            }}
          >
            404
          </Typography>

          {/* Error Message */}
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              mb: 2,
              textShadow: '0 2px 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            Page Not Found
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 400,
              mb: 4,
              maxWidth: 500,
              mx: 'auto',
            }}
          >
            Oops! The page you're looking for doesn't exist or has been moved.
          </Typography>

          {/* Action Buttons */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 4 }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Home />}
              onClick={handleGoHome}
              sx={{
                background: 'rgba(255, 255, 255, 0.95)',
                color: '#667eea',
                fontWeight: 700,
                fontSize: '1rem',
                py: 1.5,
                px: 4,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                '&:hover': {
                  background: '#ffffff',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.3)',
                },
                transition: 'all 0.3s',
              }}
            >
              Go to Home
            </Button>

            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBack />}
              onClick={handleGoBack}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '1rem',
                py: 1.5,
                px: 4,
                backdropFilter: 'blur(10px)',
                background: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  borderColor: '#ffffff',
                  background: 'rgba(255, 255, 255, 0.2)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s',
              }}
            >
              Go Back
            </Button>
          </Stack>

          {/* Additional Info */}
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              mt: 6,
            }}
          >
            If you believe this is an error, please contact support
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default NotFound;
